import { ace_index } from '@axo/shared/data-access/types';
import { debounceFunction } from '@axo/shared/util/fp';
import { InputGroup } from '@blueprintjs/core';
import { ace_index_hooks } from '@axo/shared/data-access/hooks';
import { ChangeEvent, Dispatch, SetStateAction, useMemo } from 'react';
import { IconMenu } from '../IconMenu';
import { AceCard } from '../AceCard';
import { ClearFilterButton, FilterAppliedIndicator } from '../SearchFilterCard';
import { setPageNumber } from '../SearchResults';
import styles from './QuickSearchFilters.module.css';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  clearQuery: () => void;
}

export function QuickSearchFilters({ query, setQuery, clearQuery }: Props) {
  const changeHandler = useMemo(
    () =>
      debounceFunction(
        (
          event: ChangeEvent<HTMLInputElement>,
          field: keyof ace_index.FindInput,
        ) =>
          setQuery((query) => ({
            ...setPageNumber(1)(query),
            [field]: event.target.value === '' ? undefined : event.target.value,
          })),
      ),
    [setQuery],
  );

  const results = ace_index_hooks.useACEIndexQuery(query);
  const active = results.isIdle !== true;

  return (
    <AceCard
      id="QuickSearch"
      title="Quick Search"
      leftTag={active && <FilterAppliedIndicator text="Quick Search applied" />}
    >
      <IconMenu>
        <ClearFilterButton
          title="Quick Search"
          disabled={!active}
          onClick={clearQuery}
        />
      </IconMenu>

      <div className={styles.searchButtons}>
        <InputGroup
          autoComplete="off"
          placeholder="Search by First Name"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.PersonFirstName ?? ''}
          onChange={(e) => changeHandler(e, 'PersonFirstName')}
        />

        <InputGroup
          autoComplete="off"
          placeholder="Search by Last Name"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.PersonLastName ?? ''}
          onChange={(e) => changeHandler(e, 'PersonLastName')}
        />

        <InputGroup
          autoComplete="off"
          placeholder="Search by Phone Number"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.CustomerMobilePhoneNumber ?? ''}
          onChange={(e) => changeHandler(e, 'CustomerMobilePhoneNumber')}
        />

        <InputGroup
          autoComplete="off"
          placeholder="Search by SSN"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.PersonSSN ?? ''}
          onChange={(e) => changeHandler(e, 'PersonSSN')}
        />

        <InputGroup
          autoComplete="off"
          placeholder="Search by Email"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.CustomerEmail ?? ''}
          onChange={(e) => changeHandler(e, 'CustomerEmail')}
        />

        <InputGroup
          autoComplete="off"
          placeholder="Search by Application Number"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.LoanApplicationHumanReadableID ?? ''}
          onChange={(e) => changeHandler(e, 'LoanApplicationHumanReadableID')}
        />

        <InputGroup
          autoComplete="off"
          placeholder="Quote Lender Reference"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.LoanQuoteLenderReference ?? ''}
          onChange={(e) => changeHandler(e, 'LoanQuoteLenderReference')}
          disabled={!!query.LoanRequestLenderReference}
        />
        <InputGroup
          autoComplete="off"
          placeholder="Request Lender Reference"
          asyncControl={true}
          leftIcon="search"
          type="search"
          value={query.LoanRequestLenderReference ?? ''}
          onChange={(e) => changeHandler(e, 'LoanRequestLenderReference')}
          disabled={!!query.LoanQuoteLenderReference}
        />
      </div>
    </AceCard>
  );
}
